import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { Controller, useForm } from "react-hook-form";

import { FC, useEffect } from "react";
import { PutUserFields } from "../../../interfaces/SuperAdmin/Pages/iDataUser";
import { useSingleUserControl } from "../../../context/SuperAdmin/SingleUserControlContext";
import { Estado } from "../../../interfaces/SuperAdmin/Others/iDataEstado";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const EditUserModal: FC<Props> = ({ isOpen, setIsOpen }) => {
  const { userEdit, rdsUser, isEditing, isLoadingPaises, paises } =
    useSingleUserControl();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<PutUserFields>();

  // Función para manejar el envío del formulario
  const handleSubmitForm = (data: PutUserFields) => {
    if (rdsUser) {
      userEdit(rdsUser.id_usuario, data);
    }
  };

  const handleClose = () => {
    if (!isEditing) {
      setIsOpen(false);
    }
  };

  // Cerramos el popup cuando se ejecute la función
  useEffect(() => {
    if (!isEditing && isOpen) {
      handleClose();
    }
  }, [isEditing]);

  useEffect(() => {
    if (rdsUser) {
      reset({
        nombres: rdsUser.nombres,
        apellidos: rdsUser.apellidos,
        estado: rdsUser.estado,
        pais: rdsUser.pais,
        email: rdsUser.email
      });
    }
  }, [rdsUser]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)", // Fondo negro con opacidad
      }}
    >
      <Box
        component="form"
        sx={{
          width: 400,
          bgcolor: "background.paper", // Fondo blanco del modal
          borderRadius: 2, // Bordes redondeados
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        {isLoadingPaises ? (
          <Typography
            id="edit-subscription-modal-title"
            justifyContent={"center"}
            sx={{
              width: 1,
              textAlign: "center",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Cargando...
          </Typography>
        ) : (
          <>
            <Typography
              id="edit-subscription-modal-title"
              variant="h6"
              component="h2"
            >
              Editar usuario
            </Typography>

            {/* Campo Nombre */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Nombres
              </Typography>
              <Controller
                name="nombres"
                control={control}
                rules={{ required: "El nombre es obligatorio" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    error={!!errors.nombres}
                    helperText={errors.nombres?.message}
                    disabled={isEditing}
                  />
                )}
              />
            </Box>

            {/* Campo Apellidos */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Apellidos
              </Typography>
              <Controller
                name="apellidos"
                control={control}
                rules={{ required: "El apellido es obligatorio" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    error={!!errors.apellidos}
                    helperText={errors.apellidos?.message}
                    disabled={isEditing}
                  />
                )}
              />
            </Box>

            {/* Campo Email */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Email
              </Typography>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "El email es obligatorio",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Ingresa un email válido",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isEditing}
                  />
                )}
              />
            </Box>

            {/* Campo estado */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ mt: 3, fontWeight: 500 }}>
                Estado
              </Typography>
              <Controller
                name="estado"
                control={control}
                render={({ field }) => (
                  <FormControl sx={{ mt: 1 }} fullWidth>
                    <Select
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={isEditing}
                    >
                      <MenuItem value={Estado.ACTIVO}>Activo</MenuItem>
                      <MenuItem value={Estado.INACTIVO}>Inactivo</MenuItem>
                      <MenuItem value={Estado.ELIMINADO}>Eliminado</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            {/* Campo pais */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ mt: 3, fontWeight: 500 }}>
                País
              </Typography>
              <Controller
                name="pais"
                control={control}
                render={({ field }) => (
                  <FormControl sx={{ mt: 1 }} fullWidth>
                    <Select
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={isEditing}
                    >
                      {paises.map((pais) => (
                        <MenuItem key={pais.id_pais} value={pais.codigo}>
                          {pais.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            <Box
              sx={{
                textAlign: "right",
                display: "flex",
                justifyContent: "flex-end",
                gap: 3,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setIsOpen(false)}
                disabled={isEditing}
                sx={{
                  borderRadius: 14,
                  border: "1px solid #336B87",
                  color: "#336B87",
                  "&:hover": {
                    backgroundColor: "#336B87",
                    color: "#EEF3F6",
                  },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                sx={{ background: "#336B87" }}
                disabled={isEditing}
                type="submit"
              >
                {isEditing ? "Guardando" : "Guardar"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default EditUserModal;
